import { useEffect, useMemo, useRef, useState } from 'react';
import type { ColumnDef } from '../lib/columns';

/**
 * Column visibility popover.
 *
 * A popover rather than a modal: choosing columns is a quick adjustment made
 * while looking at the table, and a full-screen dialog would hide the thing
 * being adjusted.
 *
 * The custom-field group sits below every standard column, so on a table with
 * a dozen standard columns it starts below the fold of the popover's scroll
 * area — and on macOS the overlay scrollbar is invisible until you scroll, so
 * fields simply look absent. Three things stop that: the scroll area is sized
 * against the viewport, the group headings are sticky and carry a count, and a
 * search box makes any field reachable without scrolling at all.
 */
export function ColumnPicker({
  columns,
  visibleIds,
  onToggle,
  onReset,
}: {
  columns: ColumnDef[];
  visibleIds: string[];
  onToggle: (id: string) => void;
  onReset: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Reopening starts from the whole list; a stale filter would look like
  // missing columns, which is the very problem this box exists to solve.
  useEffect(() => {
    if (open) searchRef.current?.focus();
    else setQuery('');
  }, [open]);

  const visible = new Set(visibleIds);
  const core = columns.filter((c) => c.group === 'core');
  const custom = columns.filter((c) => c.group === 'custom');
  const shownCount = columns.filter((c) => c.locked || visible.has(c.id)).length;

  const needle = query.trim().toLowerCase();
  const match = useMemo(
    () => (c: ColumnDef) => needle === '' || c.label.toLowerCase().includes(needle),
    [needle],
  );
  const coreShown = core.filter(match);
  const customShown = custom.filter(match);
  const noMatches = needle !== '' && coreShown.length === 0 && customShown.length === 0;

  const option = (column: ColumnDef) => (
    <label key={column.id} className="col-option">
      <input
        type="checkbox"
        checked={column.locked || visible.has(column.id)}
        disabled={column.locked}
        onChange={() => onToggle(column.id)}
      />
      <span>{column.label}</span>
      {column.locked && <span className="col-locked">always</span>}
    </label>
  );

  return (
    <div className="col-picker" ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className="btn"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((o) => !o)}
      >
        Columns <span style={{ color: 'var(--muted)' }}>({shownCount})</span>
      </button>

      {open && (
        <div className="col-popover" role="dialog" aria-label="Choose columns">
          <div className="col-popover-head">
            <strong style={{ fontSize: 13 }}>Show columns</strong>
            <button type="button" className="btn btn-ghost btn-sm" onClick={onReset}>
              Reset
            </button>
          </div>

          <div className="col-search">
            <input
              ref={searchRef}
              type="search"
              value={query}
              placeholder={`Search ${columns.length} columns…`}
              aria-label="Search columns"
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className="col-popover-body">
            {coreShown.length > 0 && (
              <>
                <p className="col-group-label">Standard ({core.length})</p>
                {coreShown.map(option)}
              </>
            )}

            {customShown.length > 0 && (
              <>
                <p className="col-group-label col-group-next">Custom fields ({custom.length})</p>
                {customShown.map(option)}
              </>
            )}

            {custom.length === 0 && needle === '' && (
              <>
                <p className="col-group-label col-group-next">Custom fields</p>
                <p style={{ margin: '0 0 4px', fontSize: 12.5, color: 'var(--muted)' }}>
                  None configured yet.
                </p>
              </>
            )}

            {noMatches && (
              <p style={{ margin: '2px 0 4px', fontSize: 12.5, color: 'var(--muted)' }}>
                No column matches “{query.trim()}”.
              </p>
            )}
          </div>

          <div className="col-popover-foot">Saved on this device for your account.</div>
        </div>
      )}
    </div>
  );
}
